/* eslint-disable */
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import Assets from '../entities/brand-kit-list-item';
import Api from './api';
import ApiV2 from './api-v2';
import { SanityClient } from '@sanity/client';
import { Result } from '../entities/sanity-list-item';
import imageUrlBuilder from '@sanity/image-url';
import { SanityImageSource } from '@sanity/image-url/lib/types/types';
import { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';

const listAssetsKey = 'list-assets';

export enum BrandKitAssets {
  intro = 'intro',
  logo = 'logo',
  outro = 'outro',
}

export interface BrandKitScopedAsset {
  path: string | null;
  s3Path?: string | null;
}

export interface ScopedBrandKitResponse {
  entityType?: 'event' | 'project';
  entityId?: string;
  intro: BrandKitScopedAsset;
  logo: BrandKitScopedAsset;
  outro: BrandKitScopedAsset;
}

interface ApiV2Response<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export function useGetAssets(enabled: boolean = true): UseQueryResult<Assets> {
  return useQuery(
    listAssetsKey,
    async () => {
      return await Api.get('/assets');
    },
    {
      enabled,
    }
  );
}

export function useGetScopedBrandKit(
  params: { projectId?: string; eventId?: string; organizationId?: string },
  enabled: boolean = true,
): UseQueryResult<ScopedBrandKitResponse> {
  return useQuery(
    ['scoped-brand-kit', params.projectId, params.eventId, params.organizationId],
    async () => {
      const response = await ApiV2.get<ApiV2Response<ScopedBrandKitResponse>>('/api/brandkit', {
        ...(params.projectId && { project_id: params.projectId }),
        ...(params.eventId && { event_id: params.eventId }),
        ...(params.organizationId && { organization_id: params.organizationId }),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch scoped brand kit');
      }
      return response.data;
    },
    { enabled },
  );
}

/**
 * Hook for uploading assets directly to S3 using pre-signed URLs
 * Reason: Bypasses API Gateway's 10MB limit by uploading directly to S3
 * @returns Mutation that returns the s3Path of the uploaded asset
 */
export function useUploadAssets(): UseMutationResult<
  { s3Path: string },
  unknown,
  { type: BrandKitAssets; file: File }
> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ type, file }) => {
      // Step 1: Get pre-signed URL from backend
      const response = (await Api.post(`/assets/upload-url/${type}`, {
        fileName: file.name,
      })) as unknown as { url: string; s3Path: string };
      const { url, s3Path } = response;

      // Step 2: Upload directly to S3 using the pre-signed URL
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      // Return the s3Path so it can be used to update event brandKit
      return { s3Path };
    },
    {
      onSuccess: () => queryClient.invalidateQueries([listAssetsKey]),
    },
  );
}

export function useUploadScopedBrandKitAsset(): UseMutationResult<
  { s3Path: string; entityType: 'event' | 'project'; entityId: string },
  unknown,
  {
    type: BrandKitAssets;
    file: File;
    projectId?: string;
    eventId?: string;
    entityType?: 'event' | 'project';
    entityId?: string;
  }
> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ type, file, projectId, eventId, entityType, entityId }) => {
      const response = await ApiV2.post<
        {
          success: boolean;
          data?: {
            url: string;
            s3Path: string;
            entityType: 'event' | 'project';
            entityId: string;
          };
          error?: string;
        },
        {
          fileName: string;
          projectId?: string;
          eventId?: string;
          entityType?: 'event' | 'project';
          entityId?: string;
        }
      >(`/api/brandkit/upload-url/${type}`, {
        fileName: file.name,
        ...(projectId && { projectId }),
        ...(eventId && { eventId }),
        ...(entityType && { entityType }),
        ...(entityId && { entityId }),
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to get upload URL');
      }
      const { url, s3Path, entityType: resolvedEntityType, entityId: resolvedEntityId } = response.data;
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }
      return { s3Path, entityType: resolvedEntityType, entityId: resolvedEntityId };
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['scoped-brand-kit']),
    },
  );
}

export function useUploadFonts(): UseMutationResult<void, unknown, { type: string; font: string }> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ type, font }) => {
      const data = {
        name: font,
      };
      await Api.post(`/assets/${type}`, data);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([listAssetsKey]),
    },
  );
}
export function useUploadColor(): UseMutationResult<
  void,
  unknown,
  { type: string; color: string }
> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ type, color }) => {
      const data = {
        name: color,
      };
      await Api.post(`/assets/${type}`, data);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([listAssetsKey]),
    },
  );
}

/**
 * Converts an S3 path to a signed URL
 * Reason: Event brandKit stores S3 paths that need to be converted to signed URLs for display
 */
export async function getSignedUrlFromS3Path(s3Path: string): Promise<string> {
  const response = await ApiV2.post<{ success: boolean; data?: { signedUrl: string }; error?: string }>('/api/assets/signed-url', {
    s3Path,
  });
  
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get signed URL');
  }
  
  return response.data.signedUrl;
}

export function useSanity(client: SanityClient): UseQueryResult<Result> {
  return useQuery('result', async () => {
    return await client.fetch<Result[]>('*[_type == "post"]').then((result) => {
      return result[0];
    });
  });
}

export function urlFor(source: SanityImageSource, client: SanityClient): ImageUrlBuilder {
  const builder = imageUrlBuilder(client);
  return builder.image(source);
}
